import React, { useRef, useState } from "react"
import { createPortal } from "react-dom"
import { AnimatePresence, motion, useReducedMotion } from "framer-motion"
import i18n from "i18next"
import { useTranslation } from "react-i18next"

const tr = (key, opts) => i18n.t(`admin.${key}`, opts)

const DATE_LOCALES = {
  ru: "ru-RU",
  uk: "uk-UA",
  ro: "ro-RO",
  en: "en-GB",
  bg: "bg-BG",
  cz: "cs-CZ",
}

// Геттеры, чтобы подпись бралась из текущего языка при каждом обращении
export const SOURCE_LABELS = {
  get hotel() {
    return tr("hotel")
  },
  get transport() {
    return tr("transport")
  },
}

const DAY_MS = 86400000

const LEVEL_STYLES = {
  critical: { color: "#dc2626", soft: "#fef2f2", border: "#fecaca", text: "#b91c1c" },
  warning: { color: "#ea580c", soft: "#fff7ed", border: "#fed7aa", text: "#c2410c" },
  notice: { color: "#d97706", soft: "#fffbeb", border: "#fde68a", text: "#b45309" },
}

export const parseDay = (val) => {
  if (!val) return null
  const m = String(val).match(/^(\d{4})-(\d{2})-(\d{2})/)
  if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]))
  const d = new Date(val)
  return Number.isNaN(d.getTime()) ? null : new Date(d.getFullYear(), d.getMonth(), d.getDate())
}

const todayDay = () => {
  const n = new Date()
  return new Date(n.getFullYear(), n.getMonth(), n.getDate())
}

const fmtDay = (d) => (d ? d.toLocaleDateString(i18n.language === "cz" ? "cs" : i18n.language) : "")

const pluralCount = (n) => Math.abs(Math.trunc(Number(n))) || 0

export const dayWord = (n) => tr("fines_day_word", { count: pluralCount(n) })
const nightsWord = (n) => tr("fines_night_word", { count: pluralCount(n) })

const inDaysText = (days) =>
  days === 1
    ? tr("fines_already_tomorrow")
    : tr("fines_in_days", { days: `${days} ${dayWord(days)}` })

export const formatPenaltyAmount = (p) => {
  if (!p) return ""
  const v = String(p.penalty ?? "").trim()
  if (!v) return ""
  if (p.type === "%") return `${v}%`
  if (p.type === "nights") return `${v} ${nightsWord(v)}`
  if (p.type === "fix") return tr("fines_fixed", { value: v })
  return [v, p.type].filter(Boolean).join(" ")
}

export const formatRelease = (release) => {
  const n = Number(release) || 0
  return n === 0
    ? tr("fines_no_show")
    : tr("fines_before_arrival", { nights: `${n} ${nightsWord(n)}` })
}

const startDateOf = (fromDate, release) => {
  const from = parseDay(fromDate)
  if (!from) return null
  return new Date(from.getTime() - (Number(release) || 0) * DAY_MS)
}

export const finesLevel = (alert) => {
  const d = Number(alert?.days_left) || 0
  if (d <= 1) return "critical"
  if (d <= 3) return "warning"
  return "notice"
}

export const finesBorderColor = (alert) =>
  alert ? LEVEL_STYLES[finesLevel(alert)].border : undefined

const alertDetail = (alert) =>
  [
    alert.starts_on ? tr("fines_since", { date: fmtDay(parseDay(alert.starts_on)) }) : null,
    SOURCE_LABELS[alert.source] || null,
    formatPenaltyAmount(alert) || null,
  ]
    .filter(Boolean)
    .join(" · ")

const IconWarn = ({ className = "w-3.5 h-3.5" }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"
    />
  </svg>
)

const IconClock = ({ className = "w-3.5 h-3.5" }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
    />
  </svg>
)

// ── Счётчик дней с пульсацией ────────────────────────────────────────────────

const CountdownDot = ({ days, level, size = 20 }) => {
  const reduce = useReducedMotion()
  const s = LEVEL_STYLES[level]
  const duration = level === "critical" ? 1.2 : level === "warning" ? 1.8 : 2.6

  return (
    <span
      className="relative inline-flex items-center justify-center flex-shrink-0"
      style={{ width: size, height: size }}
    >
      {!reduce && (
        <motion.span
          className="absolute inset-0 rounded-full"
          style={{ backgroundColor: s.color }}
          animate={{ scale: [1, 1.9], opacity: [0.45, 0] }}
          transition={{ duration, repeat: Infinity, ease: "easeOut" }}
        />
      )}
      <span
        className="relative inline-flex items-center justify-center rounded-full text-white font-bold leading-none"
        style={{ width: size, height: size, backgroundColor: s.color, fontSize: size * 0.5 }}
      >
        {days}
      </span>
    </span>
  )
}

// ── Поповер с деталями ───────────────────────────────────────────────────────

const AlertDetails = ({ alert, level }) => {
  useTranslation()
  const s = LEVEL_STYLES[level]
  const Icon = level === "critical" ? IconWarn : IconClock
  const days = Number(alert.days_left) || 0
  const startsOn = parseDay(alert.starts_on)

  const rows = [
    [tr("service"), SOURCE_LABELS[alert.source] || alert.source],
    [tr("condition2"), formatRelease(alert.release)],
    [tr("fines_size"), formatPenaltyAmount(alert)],
  ].filter(([, v]) => v)

  return (
    <div className="bg-white border border-gray-200 rounded-2xl shadow-xl overflow-hidden">
      <div className="h-1" style={{ backgroundColor: s.color }} />
      <div className="p-3">
        <div className="flex items-center gap-2.5">
          <span
            className="inline-flex items-center justify-center w-8 h-8 rounded-xl flex-shrink-0"
            style={{ backgroundColor: s.soft, color: s.color }}
          >
            <Icon className="w-4 h-4" />
          </span>
          <div className="min-w-0">
            <div className="text-xs font-bold text-gray-900 leading-tight">
              {tr("fines_soon_title")}
            </div>
            <div className="text-[11px] leading-tight mt-0.5" style={{ color: s.text }}>
              {inDaysText(days)}
              {startsOn ? ` · ${tr("fines_since", { date: fmtDay(startsOn) })}` : ""}
            </div>
          </div>
        </div>

        {rows.length > 0 && (
          <div className="mt-2.5 pt-2.5 border-t border-gray-100 space-y-1">
            {rows.map(([k, v]) => (
              <div key={k} className="flex items-center justify-between gap-3 text-[11px]">
                <span className="text-gray-400">{k}</span>
                <span className="font-semibold text-gray-800 text-right">{v}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

export const FinesAlertBadge = ({ alert }) => {
  useTranslation()
  const [pos, setPos] = useState(null)
  const ref = useRef(null)

  if (!alert) return null

  const level = finesLevel(alert)
  const s = LEVEL_STYLES[level]
  const days = Number(alert.days_left) || 0

  const show = () => {
    const r = ref.current?.getBoundingClientRect()
    if (!r) return
    const W = 256
    const M = 8
    const x = Math.min(Math.max(r.left + r.width / 2, W / 2 + M), window.innerWidth - W / 2 - M)
    const below = r.top < 190
    setPos({ x, y: below ? r.bottom + 8 : r.top - 8, below })
  }
  const hide = () => setPos(null)

  return (
    <>
      <motion.span
        ref={ref}
        tabIndex={0}
        initial={{ opacity: 0, scale: 0.85 }}
        animate={{ opacity: 1, scale: 1 }}
        whileHover={{ scale: 1.05 }}
        transition={{ type: "spring", stiffness: 420, damping: 26 }}
        onMouseEnter={show}
        onMouseLeave={hide}
        onFocus={show}
        onBlur={hide}
        onClick={(e) => e.stopPropagation()}
        className="inline-flex items-center gap-1.5 h-6 pl-1 pr-2 rounded-full border text-[10px] font-semibold whitespace-nowrap cursor-default outline-none"
        style={{ backgroundColor: s.soft, borderColor: s.border, color: s.text }}
      >
        <CountdownDot days={days} level={level} size={16} />
        {days === 1 ? tr("fines_badge_tomorrow") : tr("fines_badge_days", { days })}
      </motion.span>

      {createPortal(
        <AnimatePresence>
          {pos && (
            <motion.div
              key="fines-popover"
              initial={{ opacity: 0, y: pos.below ? -4 : 4, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: pos.below ? -4 : 4, scale: 0.97 }}
              transition={{ duration: 0.14, ease: "easeOut" }}
              style={{
                position: "fixed",
                top: pos.y,
                left: pos.x,
                width: 256,
                transform: `translate(-50%, ${pos.below ? "0" : "-100%"})`,
                zIndex: 9999,
              }}
              className="pointer-events-none"
            >
              <AlertDetails alert={alert} level={level} />
            </motion.div>
          )}
        </AnimatePresence>,
        document.body
      )}
    </>
  )
}

// ── Баннер (mobile) ──────────────────────────────────────────────────────────

export const FinesAlertBanner = ({ alert }) => {
  useTranslation()
  const reduce = useReducedMotion()
  if (!alert) return null

  const level = finesLevel(alert)
  const s = LEVEL_STYLES[level]
  const days = Number(alert.days_left) || 0

  return (
    <motion.div
      initial={{ opacity: 0, height: 0 }}
      animate={{ opacity: 1, height: "auto" }}
      transition={{ duration: 0.25, ease: "easeOut" }}
      style={{ overflow: "hidden" }}
      className="rounded-t-2xl"
    >
      <div
        className="relative overflow-hidden flex items-center gap-3 px-4 py-2.5 border-b"
        style={{
          background: `linear-gradient(90deg, ${s.soft}, #ffffff)`,
          borderColor: s.border,
        }}
      >
        {level === "critical" && !reduce && (
          <motion.span
            aria-hidden
            className="absolute inset-y-0 w-1/3 pointer-events-none"
            style={{
              background:
                "linear-gradient(90deg, transparent, rgba(255,255,255,0.65), transparent)",
            }}
            initial={{ x: "-100%" }}
            animate={{ x: "400%" }}
            transition={{ duration: 2.4, repeat: Infinity, repeatDelay: 1.2, ease: "easeInOut" }}
          />
        )}

        <CountdownDot days={days} level={level} size={26} />

        <div className="relative min-w-0 flex-1">
          <div className="text-xs font-bold leading-tight" style={{ color: s.text }}>
            {days === 1
              ? tr("fines_banner_tomorrow")
              : tr("fines_banner_days", { days: `${days} ${dayWord(days)}` })}
          </div>
          <div className="text-[11px] text-gray-500 truncate mt-0.5">{alertDetail(alert)}</div>
        </div>
      </div>
    </motion.div>
  )
}

// ── Цветная полоска слева строки таблицы ─────────────────────────────────────
// Родительский <td> должен иметь className="relative".

export const FinesAccent = ({ alert }) => {
  const reduce = useReducedMotion()
  if (!alert) return null

  const level = finesLevel(alert)
  const blink = level === "critical" && !reduce

  return (
    <motion.span
      aria-hidden
      className="absolute left-0 top-0 bottom-0 w-[3px] rounded-r"
      style={{ backgroundColor: LEVEL_STYLES[level].color, originY: 0 }}
      initial={{ scaleY: 0 }}
      animate={blink ? { scaleY: 1, opacity: [1, 0.35, 1] } : { scaleY: 1, opacity: 1 }}
      transition={
        blink
          ? { scaleY: { duration: 0.3 }, opacity: { duration: 1.4, repeat: Infinity } }
          : { duration: 0.3 }
      }
    />
  )
}

export const ActiveFineCell = ({ fine }) => {
  useTranslation()
  if (!fine) return <span className="text-xs text-gray-300">—</span>

  return (
    <div className="text-xs min-w-[110px]">
      <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-lg text-[11px] font-semibold bg-rose-50 text-rose-700 border border-rose-200">
        <span className="w-1.5 h-1.5 rounded-full bg-rose-500" />
        {formatPenaltyAmount(fine) || "—"}
      </span>
      <div className="text-[10px] text-gray-400 mt-0.5">{formatRelease(fine.release)}</div>
    </div>
  )
}

const TierDot = ({ status }) => {
  const reduce = useReducedMotion()

  if (status === "next") {
    return (
      <span className="relative inline-flex w-2 h-2 flex-shrink-0">
        {!reduce && (
          <motion.span
            className="absolute inset-0 rounded-full bg-amber-400"
            animate={{ scale: [1, 2.2], opacity: [0.6, 0] }}
            transition={{ duration: 1.6, repeat: Infinity, ease: "easeOut" }}
          />
        )}
        <span className="relative w-2 h-2 rounded-full bg-amber-500" />
      </span>
    )
  }

  return (
    <span
      className={`w-2 h-2 rounded-full flex-shrink-0 ${
        status === "active" ? "bg-rose-500" : "bg-gray-200"
      }`}
    />
  )
}

export const PossibleFinesCell = ({ fines, fromDate, alert }) => {
  useTranslation()
  const groups = Object.entries(fines || {}).filter(
    ([, list]) => Array.isArray(list) && list.length > 0
  )

  if (groups.length === 0) return <span className="text-xs text-gray-300">—</span>

  const today = todayDay()

  return (
    <div className="text-xs min-w-[150px] space-y-2">
      {groups.map(([source, list]) => (
        <div key={source}>
          <div className="text-[10px] uppercase tracking-wide font-semibold text-gray-400 mb-0.5">
            {SOURCE_LABELS[source] || source}
          </div>
          <div className="space-y-0.5">
            {list.map((p, i) => {
              const start = startDateOf(fromDate, p.release)
              const isNext =
                !!alert && alert.source === source && Number(alert.release) === Number(p.release)
              const status = isNext ? "next" : start && start <= today ? "active" : "future"
              const title =
                status === "active"
                  ? tr("fines_active_now")
                  : start
                    ? tr("fines_starts_on", { date: fmtDay(start) })
                    : undefined

              return (
                <div key={`${p.release}-${i}`} className="flex items-center gap-1.5" title={title}>
                  <TierDot status={status} />
                  <span className="text-gray-500 w-12 flex-shrink-0">
                    {Number(p.release) === 0
                      ? tr("fines_no_show")
                      : `${p.release} ${tr("nights_short")}`}
                  </span>
                  <span
                    className={`font-medium ${status === "future" ? "text-gray-600" : "text-gray-900"}`}
                  >
                    {formatPenaltyAmount(p)}
                  </span>
                </div>
              )
            })}
          </div>
        </div>
      ))}
    </div>
  )
}
